"use client";

import { JsonLd, faqPageSchema } from "@/components/json-ld";
import { DemoLink } from "@/components/demo-modal";
import { ProductDemoForm } from "@/components/product-demo-form";
import { AuthModal } from "@/features/auth/auth-modal";
import { ArrowRight, Check, Menu, Store, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  type FocusEvent,
  type MouseEvent,
  type PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";

const homeAsset = "/static/images/figma-home";

const businessTypes = [
  {
    icon: "business-cafe.svg",
    title: "Cafés",
    detail: "Quick orders, happy regulars",
    restaurantImage:
      "Image (Barista serving a customer at a warm café counter).png",
    restaurantMockup: "MockCard-4.png",
    restaurantImageAlt: "Barista serving a customer at a café counter",
  },
  {
    icon: "business-qsr.svg",
    title: "Quick service & food courts",
    detail: "Fast counter, busy rush hours",
    restaurantImage:
      "Image (Fresh coffee being served at a busy café counter).png",
    restaurantMockup: "MockCard.png",
    restaurantImageAlt: "Fresh coffee being served at a busy café counter",
  },
  {
    icon: "business-dining.svg",
    title: "Family & fine dining",
    detail: "Table service, split bills",
    restaurantImage:
      "Image (Friends sharing a meal around a restaurant table).png",
    restaurantMockup: "MockCard-1.png",
    restaurantImageAlt: "Friends sharing a meal around a restaurant table",
  },
  {
    icon: "business-kitchen.svg",
    title: "Cloud kitchens",
    detail: "Delivery-first kitchens",
    restaurantImage:
      "Image (Chef preparing food in a working commercial kitchen).png",
    restaurantMockup: "MockCard-2.png",
    restaurantImageAlt: "Chef preparing food in a commercial kitchen",
  },
  {
    icon: "business-bakery.svg",
    title: "Bakeries & sweet shops",
    detail: "Fresh stock, daily",
    restaurantImage: "pexels-anderson-alves-2158767102-35661532 1.png",
    restaurantMockup: "MockCard-3.png",
    restaurantImageAlt: "Fresh pastries and baked goods on display",
  },
];

const retailTypes = [
  {
    icon: "business-bakery.svg",
    title: "Fashion & footwear",
    detail: "Sizes, variants, loyalty",
    image: "pexels-rdne-5698851 1.png",
    mockup: "MockCard.png",
    imageAlt: "Clothes hanging on a rack in a fashion store",
  },
  {
    icon: "business-qsr.svg",
    title: "Electronics & mobiles",
    detail: "GST invoices, serial numbers",
    image: "pexels-dothanhyb-5874513 1.png",
    mockup: "MockCard-1.png",
    imageAlt: "Shop owner working on a laptop in an electronics store",
  },
  {
    icon: "business-kitchen.svg",
    title: "Pharmacies",
    detail: "Batches, expiry, reorder",
    image: "pexels-worldsikhorg-14797855 1.png",
    mockup: "MockCard-2.png",
    imageAlt: "Pharmacist checking medicine on a pharmacy shelf",
  },
  {
    icon: "business-dining.svg",
    title: "Chains & franchises",
    detail: "Every outlet, one view",
    image: "pexels-jimmy-liao-3615017-18367537 1.png",
    mockup: "MockCard-3.png",
    imageAlt: "Retail storefronts on a busy shopping street",
  },
];

const questions = [
  {
    question: "Does it work without internet?",
    answer:
      "Yes. Keep billing even when the internet is down. reecost syncs automatically when you’re back online.",
  },
  {
    question: "Can I use my existing computer and printer?",
    answer:
      "Yes. reecost runs on the computer, laptop or Android device you already have, and works with most thermal and A4 printers.",
  },
  {
    question: "Can you move my data from Tally or Excel?",
    answer:
      "Yes. Send us your items or menu, customers and suppliers from Tally or Excel, and our team will move them into reecost for you.",
  },
  {
    question: "Do you support my language?",
    answer:
      "Yes. reecost works in English, Hindi and other supported languages, so you and your staff can use it in the language you’re comfortable with.",
  },
  {
    question: "Will reecost work for my type of business?",
    answer:
      "Most likely, yes. reecost Retail is built for grocery, clothing, hardware, furniture, book and toy stores. reecost Restaurant is built for casual and fine dining, QSRs, cafés and breweries. It works for one counter or many outlets.",
  },
  {
    question: "I’m not very good with computers. Will I be able to use it?",
    answer:
      "Yes. reecost is made for busy owners, not accountants. We set it up, train you and your staff, and our support team is just a call away.",
  },
  {
    question: "Can I try it before I pay?",
    answer:
      "Yes. Retail shops can try reecost free for 14 days. Restaurants can book a free demo, and we’ll show you how it works with your own menu.",
  },
  {
    question: "Is my business data safe?",
    answer:
      "Yes. Your data is backed up securely in the cloud, so it stays safe even if your computer breaks. Only the people you give access to can see it.",
  },
];

function Eyebrow({ children }: PropsWithChildren) {
  return <p className="fh-eyebrow">{children}</p>;
}

const isActiveRestaurant = (path: string) =>
  path === "/restaurant" || path.startsWith("/restaurant/");

export function HomeHeader({
  announcementProduct = "Retail",
}: {
  announcementProduct?: string;
} = {}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authIntent, setAuthIntent] = useState<"login" | "signup">("login");
  const openAuth = (intent: "login" | "signup") => {
    setAuthIntent(intent);
    setAuthOpen(true);
  };
  const pathname = usePathname() ?? "/";
  const currentPath = pathname.replace(/^\/[a-z]{2}(-[A-Za-z]+)?(?=\/|$)/, "");
  // Restaurants have no free trial, so their pages promote a demo instead.
  const isRestaurantPage = isActiveRestaurant(currentPath);
  const isActive = (href: string) =>
    currentPath === href || currentPath.startsWith(`${href}/`);
  const current = (href: string) =>
    isActive(href) ? ("page" as const) : undefined;
  const productLinks = [
    { label: "Retail", href: "/retail" },
    { label: "Restaurant", href: "/restaurant" },
  ];

  return (
    <>
      <div className="fh-top">
        <div className="fh-announcement">
          {isRestaurantPage ? (
            <p>
              See <strong>reecost Restaurant</strong> on your own menu. Book a
              free demo and we’ll help you set up.{" "}
              <DemoLink>Book free demo →</DemoLink>
            </p>
          ) : (
            <p>
              Try <strong>reecost {announcementProduct}</strong> free for 14
              days. Register in minutes and start billing today.{" "}
              <a href="https://retail.reecost.com/register">
                Start free trial →
              </a>
            </p>
          )}
        </div>
        <header className="fh-header">
          <Link href="/" className="fh-brand" aria-label="reecost home">
            <Image
              src={`${homeAsset}/reecost-logo.svg`}
              width={142}
              height={24}
              alt="reecost"
              priority
            />
          </Link>
          <nav className="fh-nav" aria-label="Main navigation">
            {productLinks.map((product) => (
              <Link
                href={product.href}
                key={product.label}
                aria-current={current(product.href)}
              >
                {product.label}
              </Link>
            ))}
            <Link href="/pricing" aria-current={current("/pricing")}>
              Pricing
            </Link>
            <Link href="/about" aria-current={current("/about")}>
              Company
            </Link>
            <Link href="/blog" aria-current={current("/blog")}>
              Blog
            </Link>
            <Link href="https://support.reecost.com">Support</Link>
          </nav>
          <div className="fh-header-actions">
            <button
              className="fh-login"
              onClick={() => openAuth("login")}
              type="button"
            >
              Login
            </button>
            <button
              className="fh-button fh-button-outline fh-button-small"
              onClick={() => openAuth("signup")}
              type="button"
            >
              Sign up
            </button>
            <DemoLink className="fh-button fh-button-purple fh-button-small">
              Book Free Demo
            </DemoLink>
          </div>
          <button
            type="button"
            className="fh-menu-toggle"
            aria-expanded={menuOpen}
            aria-label={
              menuOpen ? "Close navigation menu" : "Open navigation menu"
            }
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
          {menuOpen && (
            <nav className="fh-mobile-nav" aria-label="Mobile navigation">
              <Link href="/retail" aria-current={current("/retail")}>
                Retail
              </Link>
              <Link href="/restaurant" aria-current={current("/restaurant")}>
                Restaurant
              </Link>
              <Link href="/pricing" aria-current={current("/pricing")}>
                Pricing
              </Link>
              <Link href="/about" aria-current={current("/about")}>
                Company
              </Link>
              <Link href="/blog" aria-current={current("/blog")}>
                Blog
              </Link>
              <Link href="https://support.reecost.com">Support</Link>
              <div className="fh-mobile-actions">
                <button
                  className="fh-mobile-login"
                  onClick={() => {
                    setMenuOpen(false);
                    openAuth("login");
                  }}
                  type="button"
                >
                  Login
                </button>
                <button
                  className="fh-mobile-signup"
                  onClick={() => {
                    setMenuOpen(false);
                    openAuth("signup");
                  }}
                  type="button"
                >
                  Sign up
                </button>
                <DemoLink
                  className="fh-mobile-demo"
                  onClick={() => setMenuOpen(false)}
                >
                  Book Free Demo
                </DemoLink>
              </div>
            </nav>
          )}
        </header>
      </div>
      <AuthModal
        intent={authIntent}
        onOpenChange={setAuthOpen}
        open={authOpen}
      />
    </>
  );
}

function Hero() {
  return (
    <section className="fh-hero" aria-labelledby="fh-hero-heading">
      <div className="fh-hero-copy">
        <h1 id="fh-hero-heading">
          Run your shop or
          <br className="fh-desktop-break" /> restaurant from
          <br className="fh-desktop-break" /> <span>one software.</span>
        </h1>
        <div className="fh-hero-description">
          <p>
            Stop running your business on three different software and
            notebooks. reecost keeps your whole day in one simple place.
          </p>
          <DemoLink className="fh-hero-talk-link">
            Talk to Our Team <span aria-hidden="true">→</span>
          </DemoLink>
        </div>
      </div>
      <div className="fh-hero-image">
        <Image
          className="fh-hero-photo"
          src={`${homeAsset}/hero-shop-owner.png`}
          alt="Shop owner managing his store with reecost"
          fill
          priority
          sizes="100vw"
        />
      </div>
    </section>
  );
}

export function PainPointCard({
  image,
  width,
  height,
  title,
  description,
  className = "",
}: {
  image: string;
  width: number;
  height: number;
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <article className={`fh-pain-card ${className}`}>
      <Image
        className="fh-pain-illustration"
        src={image}
        width={width}
        height={height}
        alt=""
      />
      <p>
        <strong>{title}</strong> {description}
      </p>
    </article>
  );
}

function Headaches() {
  const painPoints = [
    {
      className: "fh-pain-left-top",
      image: "Four Clean Icons for Business Challenges 3.png",
      width: 111,
      height: 86,
      title: "Stock never matches.",
      description:
        "The register says 50 packets. The shelf or store room has 38.",
    },
    {
      className: "fh-pain-right-top",
      image: "Four Clean Icons for Business Challenges 2.png",
      width: 112,
      height: 89,
      title: "Too many tools.",
      description:
        "Billing in one software, accounts in another, salaries in a notebook.",
    },
    {
      className: "fh-pain-left-bottom",
      image: "Four Clean Icons for Business Challenges 4.png",
      width: 108,
      height: 83,
      title: "Profit is a guess.",
      description:
        "You only know how the month went when the accountant tells you.",
    },
    {
      className: "fh-pain-right-bottom",
      image: "Four Clean Icons for Business Challenges 3 (1).png",
      width: 111,
      height: 87,
      title: "GST stress, every month.",
      description:
        "Days lost collecting bills and fixing mistakes before filing.",
    },
  ];

  return (
    <section className="fh-headaches fh-section">
      <div className="fh-headache-heading">
        <Eyebrow>The daily headache</Eyebrow>
        <h2>Does this sound familiar?</h2>
        <p>
          Business should feel exciting, not like chasing numbers across
          registers.
        </p>
      </div>
      <div className="fh-headache-grid">
        {painPoints.map((point) => (
          <PainPointCard
            className={point.className}
            key={point.title}
            image={`${homeAsset}/${point.image}`}
            width={point.width}
            height={point.height}
            title={point.title}
            description={point.description}
          />
        ))}
        <div className="fh-accounting-image">
          <Image
            src={`${homeAsset}/bookstore-owner.png`}
            alt="Business owner surrounded by paperwork and manually checking his accounts"
            fill
            sizes="384px"
            style={{ objectFit: "cover" }}
          />
        </div>
      </div>
    </section>
  );
}

const featureCards = [
  {
    title: "Fast Billing",
    description:
      "Bill at the counter or the table. UPI, card or cash in one tap.",
    retailImage: "Container-3.png",
    restaurantImage: "Container-7.png",
    tint: "lavender",
    icon: "✓",
  },
  {
    title: "Live Stock",
    description:
      "Stock updates with every sale. Get alerts before anything runs out.",
    retailImage: "Container-2.png",
    restaurantImage: "Container-6.png",
    tint: "mint",
    icon: "✓",
  },
  {
    title: "Suppliers & Purchase",
    description:
      "Order from vendors and see every pending payment in one list.",
    retailImage: "Container-4.png",
    restaurantImage: "Container-5.png",
    tint: "yellow",
    icon: "✓",
  },
  {
    title: "Customer Loyalty",
    description: "Points, offers and WhatsApp updates bring customers back.",
    retailImage: "Container-5.png",
    restaurantImage: "Container-4.png",
    tint: "rose",
    icon: "✓",
  },
  {
    title: "Accounts & GST",
    description:
      "Books and GST reports made from your daily bills, ready for your CA.",
    retailImage: "Container-6.png",
    restaurantImage: "Container-3.png",
    tint: "blue",
    icon: "✓",
  },
  {
    title: "Staff Salary",
    description: "Attendance and shifts tracked. Salaries ready at month end.",
    retailImage: "Container-7.png",
    restaurantImage: "Container-2.png",
    tint: "purple",
    icon: "✓",
  },
  {
    title: "Owner App",
    description:
      "Today's sales, stock and profit on your phone, wherever you are.",
    retailImage: "Container-1.png",
    restaurantImage: "Container-1.png",
    tint: "aqua",
    icon: "✓",
  },
  {
    title: "Many Outlets",
    description: "Run 2 outlets or 20, and see all of them in one place.",
    retailImage: "Container.png",
    restaurantImage: "Container.png",
    tint: "orange",
    icon: "✓",
  },
];

const featureWeekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function FeatureMockup({ index }: { index: number }) {
  if (index === 0) {
    return (
      <div className="fh-feature-demo fh-demo-billing">
        <div className="fh-table-grid">
          {["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8"].map((table) => (
            <span key={table}>{table}</span>
          ))}
        </div>
        <div className="fh-order">
          <b>Table T2</b>
          <span>
            Paneer Tikka <i>₹320</i>
          </span>
          <span>
            Butter Naan ×2 <i>₹120</i>
          </span>
          <span>
            Masala Chai <i>₹200</i>
          </span>
          <b className="fh-demo-total">
            Total <i>₹640</i>
          </b>
          <div className="fh-payment-buttons">
            <span>UPI</span>
            <span>Card</span>
            <span>Cash</span>
          </div>
        </div>
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="fh-feature-demo fh-demo-stock">
        <div className="fh-demo-topline">
          <b>Live stock</b>
          <span>Updated now</span>
        </div>
        {["Paneer", "Tomatoes", "Cooking Oil"].map((name, itemIndex) => (
          <div className="fh-stock-row" key={name}>
            <div>
              <b>{name}</b>
              <span>{["2.4 kg", "6 kg", "3 L"][itemIndex]}</span>
            </div>
            <i>
              <span style={{ width: `${[22, 46, 31][itemIndex]}%` }} />
            </i>
          </div>
        ))}
        <span className="fh-stock-alert">Paneer running low</span>
      </div>
    );
  }

  if (index === 2) {
    return (
      <div className="fh-feature-demo fh-demo-purchase">
        <div className="fh-purchase-order">
          <small>PURCHASE ORDER</small>
          <b>Fresh Veg Supplier</b>
          <span>PO #214 · Due Friday</span>
        </div>
        <b className="fh-pending-label">
          Pending payments <span>₹28,400</span>
        </b>
        {["Fresh Veg Supplier", "Dairy Supplier", "Kitchen Supplies"].map(
          (name, itemIndex) => (
            <div className="fh-purchase-row" key={name}>
              <span>
                {name}
                <small>Due 12 Jun</small>
              </span>
              <b>{["₹12,800", "₹9,600", "₹6,000"][itemIndex]}</b>
            </div>
          ),
        )}
      </div>
    );
  }

  if (index === 3) {
    return (
      <div className="fh-feature-demo fh-demo-loyalty">
        <div className="fh-points">
          18<small>Visits</small>
        </div>
        <div className="fh-loyalty-note">
          <b>Riya Sharma</b>
          <span>Customer since 2023</span>
          <div className="fh-loyalty-stats">
            <b>
              18<small>Visits</small>
            </b>
            <b>
              340<small>Points</small>
            </b>
          </div>
          <span className="fh-loyalty-offer">Send Offer</span>
          <span>You have a special offer waiting!</span>
        </div>
      </div>
    );
  }

  if (index === 4) {
    return (
      <div className="fh-feature-demo fh-demo-accounts">
        <div className="fh-report-heading">
          <b>GST summary · May</b>
          <span>Mixed slabs</span>
        </div>
        <div className="fh-tax-grid">
          <span>
            Taxable value<b>₹2,84,200</b>
          </span>
          <span>
            CGST<b>₹9,605</b>
          </span>
          <span>
            SGST<b>₹9,605</b>
          </span>
          <span>
            Total<b>₹3,03,410</b>
          </span>
        </div>
        <div className="fh-report-chart" aria-hidden="true">
          {[30, 48, 65, 53, 78, 57, 91].map((height) => (
            <i key={height} style={{ height: `${height}%` }} />
          ))}
        </div>
        <b className="fh-export-button">Export for CA</b>
      </div>
    );
  }

  if (index === 5) {
    return (
      <div className="fh-feature-demo fh-demo-attendance">
        <div className="fh-attendance-heading">
          <b>Weekly attendance</b>
          <span>12–18 May</span>
        </div>
        <div className="fh-attendance-grid">
          <span />
          {featureWeekdays.map((day) => (
            <span key={day}>{day[0]}</span>
          ))}
          {["Cashier", "Store Helper", "Manager"].map((role, roleIndex) => (
            <div className="fh-attendance-row" key={role}>
              <span>{role}</span>
              {featureWeekdays.map((day, dayIndex) => (
                <i
                  className={
                    dayIndex === roleIndex + 3 ? "is-highlighted" : undefined
                  }
                  key={day}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="fh-shift-tags">
          <span>Morning</span>
          <span>Evening</span>
        </div>
        <b className="fh-salary-ready">Salary ready · ₹24,000</b>
      </div>
    );
  }

  if (index === 6) {
    return (
      <div className="fh-feature-demo fh-demo-owner-app">
        <div className="fh-phone">
          <div className="fh-phone-speaker" />
          <small>Today</small>
          <b>₹48,250</b>
          <span>Sales</span>
          <svg viewBox="0 0 140 32" role="img" aria-label="Sales trend">
            <path d="M2 25 C18 23 19 8 35 13 S54 29 70 17 89 10 101 14 119 23 138 4" />
          </svg>
          <div className="fh-phone-stats">
            <span>
              Profit<b>₹11,300</b>
            </span>
            <span>
              Bills<b>182</b>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fh-feature-demo fh-demo-outlets">
      <div className="fh-outlet-select">
        All outlets <span>⌄</span>
      </div>
      <b className="fh-outlet-title">Today's sales</b>
      {[
        ["Koramangala", "₹42,300"],
        ["Indiranagar", "₹36,240"],
        ["HSR Layout", "₹29,680"],
        ["Whitefield", "₹21,340"],
      ].map(([outlet, sales], outletIndex) => (
        <div className="fh-outlet-row" key={outlet}>
          <span>
            {outlet}
            <b>{sales}</b>
          </span>
          <i>
            <span style={{ width: `${[88, 75, 62, 48][outletIndex]}%` }} />
          </i>
        </div>
      ))}
    </div>
  );
}

function ProductFeatures() {
  const [activeType, setActiveType] = useState<"retail" | "restaurant">(
    "restaurant",
  );
  const featureTrackRef = useRef<HTMLDivElement>(null);
  const featureImageDirectory =
    activeType === "retail" ? "feature%20retail" : "feature%20restuarant";

  function updateFeatureProgress() {
    const track = featureTrackRef.current;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const progress = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
    const progressBar = track.parentElement?.querySelector<HTMLElement>(
      ".fh-feature-progress",
    );
    progressBar?.style.setProperty("--feature-progress", `${progress * 88}%`);
  }

  return (
    <section className="fh-features">
      <div className="fh-section fh-feature-inner">
        <Eyebrow>Everything in one place</Eyebrow>
        <h2>
          One system for the <span>counter, the kitchen</span> and the books
        </h2>
        <p className="fh-feature-intro">
          Built for restaurants and retail stores. Bill faster through the day,
          and stay on top of stock, staff and GST without the paperwork.
        </p>
        <fieldset
          className="fh-feature-switcher"
          aria-label="Choose a reecost product"
        >
          <button
            type="button"
            aria-pressed={activeType === "retail"}
            onClick={() => setActiveType("retail")}
          >
            reecost Retail
          </button>
          <button
            type="button"
            aria-pressed={activeType === "restaurant"}
            onClick={() => setActiveType("restaurant")}
          >
            reecost Restaurant
          </button>
        </fieldset>
        <section
          className="fh-feature-track"
          onScroll={updateFeatureProgress}
          ref={featureTrackRef}
          aria-label="reecost features"
        >
          {featureCards.map((feature, index) => (
            <article
              className={`fh-feature-card fh-feature-${feature.tint}`}
              key={feature.title}
            >
              <div className={`fh-feature-preview fh-tint-${feature.tint}`}>
                {activeType === "retail" || activeType === "restaurant" ? (
                  <Image
                    className="fh-feature-retail-image"
                    src={`/static/images/Homepage-Landing%20Page/${featureImageDirectory}/${activeType === "retail" ? feature.retailImage : feature.restaurantImage}`}
                    alt=""
                    fill
                    sizes="(max-width: 700px) 82vw, 380px"
                  />
                ) : (
                  <FeatureMockup index={index} />
                )}
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              <span className={`fh-included fh-included-${feature.tint}`}>
                <span className="fh-included-icon" aria-hidden="true">
                  <Check />
                </span>
                Included in reecost
              </span>
            </article>
          ))}
        </section>
        <div className="fh-feature-progress" aria-hidden="true">
          <span />
        </div>
      </div>
    </section>
  );
}

const steps = [
  {
    title: "Take a free demo",
    text: "See reecost with your own business needs.",
    image: "DemoMockup.png",
  },
  {
    title: "Choose your plan",
    text: "Simple plans. No surprise add-ons.",
    image: "PlanMockup.png",
  },
  {
    title: "We set up & train",
    text: "We move data and train your staff.",
    image: "SetupMockup.png",
  },
  {
    title: "Start billing",
    text: "Go live with support by your side.",
    image: "BillMockup.png",
  },
];

function StepPreview({ image }: { image: string }) {
  return (
    <div className="fh-step-preview">
      <Image
        src={`/static/images/Homepage-Landing%20Page/Set-up%20Section/${image}`}
        alt=""
        fill
        sizes="(max-width: 600px) 100vw, (max-width: 1100px) 50vw, 25vw"
      />
    </div>
  );
}

function HowItWorks() {
  return (
    <section className="fh-how fh-section">
      <div className="fh-how-heading">
        <Eyebrow>How it works</Eyebrow>
        <h2>
          From demo to first bill in
          <br />
          <span>four simple steps</span>
        </h2>
        <p>
          We handle the setup, your data and staff training, so you can start
          billing without the stress.
        </p>
      </div>
      <div className="fh-steps">
        {steps.map((step, index) => (
          <article className="fh-step" key={step.title}>
            <div className="fh-step-number">{index + 1}</div>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
            <StepPreview image={step.image} />
          </article>
        ))}
      </div>
      <div className="fh-how-actions">
        <DemoLink className="fh-button fh-button-purple">
          Book Free Demo
        </DemoLink>
        <Link href="/pricing" className="fh-text-link">
          See plans <ArrowRight aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

export function ForYourBusiness({
  lockedType,
}: {
  lockedType?: "restaurant" | "retail";
} = {}) {
  const [selectedType, setActiveType] = useState<"restaurant" | "retail">(
    "restaurant",
  );
  const activeType = lockedType ?? selectedType;
  const [activeBusiness, setActiveBusiness] = useState(0);
  const [isPointerOverContent, setIsPointerOverContent] = useState(false);
  const [isContentFocused, setIsContentFocused] = useState(false);
  const businessProgressRef = useRef<HTMLSpanElement>(null);
  const elapsedRef = useRef(0);
  const isRestaurant = activeType === "restaurant";
  const types = isRestaurant ? businessTypes : retailTypes;
  const business = types[activeBusiness] ?? types[0];
  const audienceAsset = isRestaurant
    ? "/static/images/Restaurant%20page/who%20it%20is%20for"
    : "/static/images/Retail%20page/who%20is%20it%20for%20section";
  const businessImage =
    "restaurantImage" in business ? business.restaurantImage : business.image;
  const businessMockup =
    "restaurantMockup" in business
      ? business.restaurantMockup
      : business.mockup;
  const businessImageAlt =
    "restaurantImageAlt" in business
      ? business.restaurantImageAlt
      : business.imageAlt;
  const isPaused = isPointerOverContent || isContentFocused;
  const nextBusinessIndex = (activeBusiness + 1) % types.length;

  useEffect(() => {
    if (isPaused) return;

    let frameId = 0;
    let previousTime = 0;
    const advance = (time: number) => {
      if (previousTime === 0) previousTime = time;
      elapsedRef.current += time - previousTime;
      previousTime = time;

      const progress = Math.min(elapsedRef.current / 5000, 1);
      businessProgressRef.current?.style.setProperty(
        "transform",
        `scaleX(${progress})`,
      );

      if (progress === 1) {
        elapsedRef.current = 0;
        setActiveBusiness(nextBusinessIndex);
        return;
      }

      frameId = requestAnimationFrame(advance);
    };

    frameId = requestAnimationFrame(advance);
    return () => cancelAnimationFrame(frameId);
  }, [isPaused, nextBusinessIndex]);

  function selectBusiness(index: number) {
    elapsedRef.current = 0;
    if (businessProgressRef.current) {
      businessProgressRef.current.style.transform = "scaleX(0)";
    }
    setActiveBusiness(index);
  }

  function selectType(type: "restaurant" | "retail") {
    setActiveType(type);
    selectBusiness(0);
  }

  function handleContentBlur(event: FocusEvent<HTMLDivElement>) {
    const nextFocusedElement = event.relatedTarget;
    if (
      !(nextFocusedElement instanceof Node) ||
      !event.currentTarget.contains(nextFocusedElement)
    ) {
      setIsContentFocused(false);
    }
  }

  function handleContentFocus(event: FocusEvent<HTMLDivElement>) {
    if (
      event.target instanceof HTMLElement &&
      event.target.matches(":focus-visible")
    ) {
      setIsContentFocused(true);
    }
  }

  return (
    <section className="fh-audience">
      <div className="fh-section">
        <div className="fh-audience-heading">
          <div>
            <Eyebrow>Who it’s for</Eyebrow>
            <h2>
              Made for
              <br />
              <span>the way you sell</span>
            </h2>
          </div>
          <div className="fh-audience-description">
            <p>
              From a single café counter to a chain of supermarkets, reecost
              fits how your business already runs.
            </p>
            {!lockedType && (
              <div className="fh-segmented">
                <button
                  type="button"
                  aria-pressed={activeType === "retail"}
                  onClick={() => selectType("retail")}
                >
                  reecost Retail
                </button>
                <button
                  type="button"
                  aria-pressed={activeType === "restaurant"}
                  onClick={() => selectType("restaurant")}
                >
                  reecost Restaurant
                </button>
              </div>
            )}
          </div>
        </div>
        <div
          className="fh-audience-content"
          onPointerEnter={() => setIsPointerOverContent(true)}
          onPointerLeave={() => setIsPointerOverContent(false)}
          onFocusCapture={handleContentFocus}
          onBlurCapture={handleContentBlur}
        >
          <div
            className="fh-business-list"
            role="tablist"
            aria-label="Business types"
          >
            {types.map((item, index) => {
              return (
                <button
                  className={`fh-business-tab ${index === activeBusiness ? "is-active" : ""}`}
                  type="button"
                  role="tab"
                  aria-selected={index === activeBusiness}
                  key={item.title}
                  onClick={() => selectBusiness(index)}
                >
                  <span className="fh-business-icon">
                    <Image
                      src={`${homeAsset}/${item.icon}`}
                      alt=""
                      width={21}
                      height={21}
                    />
                  </span>
                  <span className="fh-business-copy">
                    <b>{item.title}</b>
                    <small>{item.detail}</small>
                  </span>
                  {index === activeBusiness && (
                    <span
                      className="fh-business-tab-progress"
                      aria-hidden="true"
                    >
                      <span ref={businessProgressRef} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <article className="fh-business-card" role="tabpanel">
            <div className="fh-business-photo">
              <Image
                src={`${audienceAsset}/${encodeURIComponent(businessImage)}`}
                alt={businessImageAlt}
                fill
                sizes="(max-width: 900px) 100vw, 778px"
                style={{ objectFit: "cover" }}
              />
              <div className="fh-bill-overlay fh-business-mockup">
                <Image
                  src={`${audienceAsset}/${encodeURIComponent(businessMockup)}`}
                  alt=""
                  fill
                  sizes="(max-width: 900px) calc(100vw - 64px), 560px"
                  style={{ objectFit: "cover" }}
                />
              </div>
            </div>
            <div className="fh-business-info">
              <h3>{business.title}</h3>
              <div className="fh-business-detail">
                <p>
                  {activeType === "restaurant"
                    ? "Bill in seconds at the counter and keep regulars coming back with points and offers."
                    : "Keep billing moving and stay on top of stock, staff and customers from one place."}
                </p>
                <Link href="/p-form">
                  See how it works <ArrowRight aria-hidden="true" />
                </Link>
              </div>
              <div className="fh-tags">
                <span>Most used:</span>
                <b>Fast Billing</b>
                <b>Customer Loyalty</b>
                <b>Owner App</b>
              </div>
            </div>
          </article>
        </div>
        <div className="fh-scale-note">
          <span>Works the same at every size:</span>
          <b>
            <Store aria-hidden="true" />1 counter
          </b>
          <b>
            <Store aria-hidden="true" />1 outlet
          </b>
          <b>
            <Store aria-hidden="true" />
            20 outlets
          </b>
        </div>
      </div>
    </section>
  );
}

export function PricingBanner() {
  return (
    <div className="fh-pricing-banner">
      <div className="fh-pricing-copy">
        <Eyebrow>Simple pricing</Eyebrow>
        <h2>
          Starts at <span>₹499/year</span>
        </h2>
        <p>
          No hidden charges. No long setup. Just the tools your business needs.
        </p>
        <a
          href="https://retail.reecost.com/register"
          className="fh-button fh-button-yellow"
        >
          Start free trial <ArrowRight aria-hidden="true" />
        </a>
      </div>
      <div className="fh-pricing-image">
        <Image
          src={`${homeAsset}/pricing-owner.png`}
          alt="Business owner using reecost at the counter"
          fill
          sizes="488px"
          loading="eager"
          style={{ objectFit: "cover" }}
        />
      </div>
    </div>
  );
}

function PricingAndFaq() {
  return (
    <section className="fh-pricing-faq">
      <PricingBanner />
      <div className="fh-faq" id="faqs">
        <h2>Frequently Asked Questions</h2>
        <JsonLd data={faqPageSchema(questions)} />
        <div className="fh-faq-list">
          {questions.map((item, index) => (
            <details key={item.question} open={index === 0}>
              <summary>
                {item.question}
                <Image
                  src={`${homeAsset}/caret-down.svg`}
                  width={20}
                  height={20}
                  alt=""
                />
              </summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoCallout() {
  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 pb-12 sm:px-6 lg:px-0 lg:pb-20">
      <div className="grid gap-10 rounded-[32px] bg-[radial-gradient(circle_at_92%_8%,rgba(201,184,255,0.25),transparent_35%),linear-gradient(153deg,#2a0b66_0%,#351080_46%,#4e1fb8_100%)] p-6 shadow-[0_28px_70px_rgba(42,11,102,0.14)] sm:p-10 md:grid-cols-[minmax(0,1fr)_minmax(380px,480px)] md:items-center md:gap-8 lg:min-h-[600px] lg:px-12 lg:py-[72px]">
        <div className="max-w-[440px] text-white">
          <p className="font-medium text-[#c9b8ff] text-xs uppercase tracking-[0.08em]">
            Your first step is free
          </p>
          <h2 className="mt-4 font-semibold text-4xl leading-[1.12] tracking-[-0.9px] sm:text-[44px]">
            See reecost work
            <br />
            <span className="text-[#edb232]">for your business.</span>
          </h2>
          <p className="mt-5 text-base text-white/75 leading-7 sm:text-lg">
            Book a free, no-pressure demo. We’ll call within 2 working hours.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {["Free demo", "Set up for you", "Staff training included"].map(
              (benefit) => (
                <span
                  key={benefit}
                  className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-white/85 text-xs"
                >
                  {benefit}
                </span>
              ),
            )}
          </div>
        </div>
        <ProductDemoForm productName="Retail & Restaurant" />
      </div>
    </section>
  );
}

export function HomeFooter() {
  const [authOpen, setAuthOpen] = useState(false);
  const scrollToFaqs = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById("faqs");
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const staticGroups = [
    {
      heading: "reecost Retail",
      href: "/retail",
      links: [
        { label: "Book Store", href: "/retail/book-store" },
        { label: "Clothing Store", href: "/retail/clothing-store" },
        { label: "Furniture Store", href: "/retail/furniture-store" },
        { label: "Grocery Store", href: "/retail/grocery-store" },
        { label: "Hardware Store", href: "/retail/hardware-store" },
        { label: "Toy Store", href: "/retail/toy-store" },
      ],
    },
    {
      heading: "reecost Restaurant",
      href: "/restaurant",
      links: [
        { label: "Restaurant", href: "/restaurant" },
        { label: "Fast Food (QSR)", href: "/restaurant/qsr" },
        { label: "Cafe & Coffee House", href: "/restaurant/cafe" },
        { label: "Brewery", href: "/restaurant/brewery" },
        { label: "Fine Dining", href: "/restaurant/fine-dining" },
        { label: "Casual Dining", href: "/restaurant/casual-dining" },
      ],
    },
  ];

  return (
    <footer className="fh-footer">
      <div className="fh-footer-main">
        <div className="fh-footer-contact">
          <Image
            src={`${homeAsset}/footer-logo.svg`}
            width={142}
            height={24}
            alt="reecost"
          />
          <p>
            <b>Not sure which plan is right for you?</b>
            <br />
            Tell us about your business and we’ll help you choose.
          </p>
          <DemoLink className="fh-button fh-button-yellow">
            Talk to Our Team <ArrowRight aria-hidden="true" />
          </DemoLink>
        </div>
        <div className="fh-footer-group">
          <h3>Company</h3>
          <Link href="/about">About us</Link>
          <button
            className="fh-footer-link-button"
            onClick={() => setAuthOpen(true)}
            type="button"
          >
            Member Login
          </button>
          <Link href="https://support.reecost.com">Support</Link>
          <Link href="/blog">Blogs</Link>
          <Link href="/#faqs" onClick={scrollToFaqs}>
            FAQs
          </Link>
          <h3 className="fh-connect-heading">Connect</h3>
          <Link
            className="fh-footer-social"
            href="https://www.instagram.com/reecost.official"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              src={`${homeAsset}/social-instagram.svg`}
              alt=""
              width={21}
              height={21}
            />
            Instagram
          </Link>
          <Link
            className="fh-footer-social"
            href="https://www.linkedin.com/company/reecostretailandrestaurant"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              src={`${homeAsset}/social-linkedin.svg`}
              alt=""
              width={21}
              height={21}
            />
            LinkedIn
          </Link>
        </div>
        {staticGroups.map((group) => (
          <div className="fh-footer-group" key={group.heading}>
            <h3>
              <Link className="fh-footer-heading-link" href={group.href}>
                {group.heading}
              </Link>
            </h3>
            {group.links.map(({ label, href }) => (
              <Link href={href} key={label}>
                {label}
              </Link>
            ))}
          </div>
        ))}
      </div>
      <div className="fh-footer-bottom">
        <div>
          <Link href="/privacy-policy">Privacy Policy</Link>
          <Link href="/cookie-policy">Cookie Policy</Link>
          <Link href="/terms-of-service">Terms of Service</Link>
        </div>
        <span>@ reecost 2026</span>
      </div>
      <AuthModal intent="login" onOpenChange={setAuthOpen} open={authOpen} />
    </footer>
  );
}

export default function FigmaHomepage() {
  return (
    <main className="figma-homepage">
      <HomeHeader />
      <Hero />
      <Headaches />
      <ProductFeatures />
      <HowItWorks />
      <ForYourBusiness />
      <PricingAndFaq />
      <DemoCallout />
      <HomeFooter />
    </main>
  );
}
